import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/autenticacao.jsx';
import { useModelosColeta } from '../../lib/coleta.js';
import { Aviso, Carregando } from '../../componentes/ui.jsx';

/**
 * "Modelo de coleta de dados": o que a IA tem por objetivo produzir em cada
 * conversa — um horario (Agendamento), uma Ordem de servico ou uma Ficha
 * clinica. Muda tambem onde a Agenda fica no menu (ver Layout.jsx).
 *
 * Os cartoes e a lista de campos vem da API (modules/registros/modelos.js):
 * modelo ou campo novo la aparece aqui sem mexer nesta tela.
 */
export function ModeloColeta() {
  const queryClient = useQueryClient();
  const { podeAcessar } = useAuth();
  const modelos = useModelosColeta();
  const trocar = useMutation({
    mutationFn: (modelo) => api.put('/api/registros/modelo', { modelo }),
    onSuccess: (dados) => {
      queryClient.setQueryData(['registros', 'modelos'], dados);
      queryClient.invalidateQueries({ queryKey: ['registros'] });
    }
  });

  if (modelos.isLoading) return <Carregando />;
  if (modelos.isError) return <Aviso tom="perigo">{modelos.error.message}</Aviso>;

  const { ativo, modelos: lista } = modelos.data;
  const atual = lista.find((m) => m.chave === ativo);
  const podeTrocar = podeAcessar('admin');

  return (
    <section className="ci-bloco">
      <header className="ci-bloco__topo">
        <div>
          <h2 className="ci-titulo-secao">
            <span aria-hidden="true">🧾</span> Modelo de coleta de dados
          </h2>
          <p className="texto-fraco">
            O objetivo da IA em cada conversa. Em <strong>Ordem de serviço</strong> e <strong>Ficha clínica</strong>, ela
            pergunta e preenche os campos sozinha, e a Agenda passa a ficar dentro dessa tela.
          </p>
        </div>
        <span className="ci-pilula">Ativo: {atual?.rotulo}</span>
      </header>

      {trocar.isError && <Aviso tom="perigo">{trocar.error.message}</Aviso>}

      <div className="ci-modos" role="radiogroup" aria-label="Modelo de coleta de dados">
        {lista.map((m) => {
          const selecionado = m.chave === ativo;
          const escolher = () => !selecionado && podeTrocar && !trocar.isPending && trocar.mutate(m.chave);
          return (
            <div
              key={m.chave}
              role="radio"
              aria-checked={selecionado}
              aria-disabled={!podeTrocar}
              tabIndex={0}
              className={`ci-modo ${selecionado ? 'ci-modo--ativo' : ''}`}
              onClick={escolher}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  escolher();
                }
              }}
            >
              <div className="ci-modo__topo">
                <span className="ci-modo__icone" aria-hidden="true">{m.icone}</span>
                {m.campos.length > 0 && (
                  <span className="ci-selo ci-selo--primario">
                    {m.campos.length} CAMPOS · {m.campos.filter((c) => c.obrigatorio).length} OBRIGATÓRIOS
                  </span>
                )}
              </div>
              <h3>{m.rotulo}</h3>
              <p className="texto-fraco">{m.descricao}</p>
              {m.campos.length > 0 && (
                <p className="ci-modo__sub">
                  {m.campos.map((c) => `${c.rotulo}${c.obrigatorio ? '*' : ''}`).join(' · ')}
                </p>
              )}
              <span className="ci-modo__marca">
                <span className={`ci-radio ${selecionado ? 'ci-radio--ligado' : ''}`} aria-hidden="true" />
                {selecionado ? 'Selecionado' : 'Selecionar'}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
